import { fromEnumAsString, toEnumFromString } from '../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../../dto/dto.js';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../generic-order-entry.js';
import { OnlineTransactionDetails } from '../../online-transaction-details.js';
import { BusinessModuleRef } from '../../business-module-ref.js';
import { EventBookingStrategy } from '../../event-booking-protocol/event-booking-strategy.js';
import { TimeZone } from '../../../../time/timezone.js';
import { TicketSupplementRef } from '../../ticket-supplement-ref.js';
import { Dayjs } from 'dayjs';
import { ElectronicDeliveryMethod } from '../../electronic-delivery-method.js';
import { Refund } from '../../discounts/refund.js';
import { OfflineTransactionDetails } from '../../offline-transaction-details.js';
import { Price } from '../../price.js';
import { InvoiceVat } from '../../invoicing/invoice-vat.js';

export class GenericOrderTicketSupplementEntry extends GenericOrderEntry {
  static readonly TYPE_ID: string = 'f4e979cac6174fe4bb0ef28a9abed4c6';
  static readonly TYPE_NAME: string = 'TicketSupplement';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly uniqueId: string,
    public readonly orderId: string,
    public readonly parentEntryId: string,
    public readonly entryGroupId: string | null,
    public readonly note: string | null,
    public readonly status: GenericOrderEntryStatus,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly eventId: string,
    public readonly bookingStrategy: EventBookingStrategy,
    public readonly onlineTransactionDetails: OnlineTransactionDetails | null,
    public readonly offlineTransactionDetails: OfflineTransactionDetails | null,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly businessRef: BusinessModuleRef,
    public readonly supplements: TicketSupplementRef[],
    public readonly deliveryMethod: ElectronicDeliveryMethod,
    public readonly refunds: Refund[],
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderTicketSupplementEntry.TYPE_ID;
  }

  get $typeName(): string {
    return GenericOrderTicketSupplementEntry.TYPE_NAME;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GenericOrderTicketSupplementEntry.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    orderId: string,
    parentEntryId: string,
    entryGroupId: string | null,
    note: string | null,
    status: GenericOrderEntryStatus,
    creatorId: string,
    creationDate: Dayjs,
    eventId: string,
    bookingStrategy: EventBookingStrategy,
    onlineTransactionDetails: OnlineTransactionDetails | null,
    offlineTransactionDetails: OfflineTransactionDetails | null,
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
    businessRef: BusinessModuleRef,
    supplements: TicketSupplementRef[],
    deliveryMethod: ElectronicDeliveryMethod,
    refunds?: Refund[],
  }): GenericOrderTicketSupplementEntry {
    return new GenericOrderTicketSupplementEntry(
        args.uniqueId,
        args.orderId,
        args.parentEntryId,
        args.entryGroupId,
        args.note,
        args.status,
        args.creatorId,
        args.creationDate,
        args.eventId,
        args.bookingStrategy,
        args.onlineTransactionDetails,
        args.offlineTransactionDetails,
        args.cancelledDate,
        args.cancelledBy,
        args.businessRef,
        args.supplements,
        args.deliveryMethod,
        args.refunds ?? [],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderTicketSupplementEntry {
    return GenericOrderTicketSupplementEntry.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      orderId: MapUtils.getString(map, 'orderId'),
      parentEntryId: MapUtils.getString(map, 'parentEntryId'),
      entryGroupId: MapUtils.getStringOrNull(map, 'entryGroupId'),
      note: MapUtils.getStringOrNull(map, 'note'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GenericOrderEntryStatus),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      eventId: MapUtils.getString(map, 'eventId'),
      bookingStrategy: toEnumFromString(MapUtils.getString(map, 'bookingStrategy'), EventBookingStrategy),
      onlineTransactionDetails: context.fromDataOrNull<OnlineTransactionDetails>(MapUtils.getRecordOrNull(map, 'onlineTransactionDetails')),
      offlineTransactionDetails: context.fromDataOrNull<OfflineTransactionDetails>(MapUtils.getRecordOrNull(map, 'offlineTransactionDetails')),
      cancelledDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      supplements: MapUtils.getList<TicketSupplementRef>(map, 'supplements', (value) => {
        return context.fromData<TicketSupplementRef>(value as SerializedData);
      }),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      refunds: MapUtils.getList<Refund>(map, 'refunds', (value) => {
        return context.fromData<Refund>(value as SerializedData);
      }),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.orderId,
      'parentEntryId': this.parentEntryId,
      'entryGroupId': this.entryGroupId,
      'note': this.note,
      '$status': fromEnumAsString(this.status),
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'eventId': this.eventId,
      'bookingStrategy': fromEnumAsString(this.bookingStrategy),
      'onlineTransactionDetails': context.toDataOrNull(this.onlineTransactionDetails),
      'offlineTransactionDetails': context.toDataOrNull(this.offlineTransactionDetails),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'businessRef': this.businessRef.encode(),
      'supplements': this.supplements.map((e) => context.toData(e)),
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
      'refunds': this.refunds.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    orderId = this.orderId,
    parentEntryId = this.parentEntryId,
    entryGroupId = this.entryGroupId,
    note = this.note,
    status = this.status,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    eventId = this.eventId,
    bookingStrategy = this.bookingStrategy,
    onlineTransactionDetails = cloneDataOrNull(this.onlineTransactionDetails),
    offlineTransactionDetails = cloneDataOrNull(this.offlineTransactionDetails),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    businessRef = cloneData(this.businessRef),
    supplements = cloneListOfCloneable(this.supplements),
    deliveryMethod = this.deliveryMethod,
    refunds = cloneListOfCloneable(this.refunds),
  }: {
    uniqueId?: string,
    orderId?: string,
    parentEntryId?: string,
    entryGroupId?: string | null,
    note?: string | null,
    status?: GenericOrderEntryStatus,
    creatorId?: string,
    creationDate?: Dayjs,
    eventId?: string,
    bookingStrategy?: EventBookingStrategy,
    onlineTransactionDetails?: OnlineTransactionDetails | null,
    offlineTransactionDetails?: OfflineTransactionDetails | null,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
    businessRef?: BusinessModuleRef,
    supplements?: TicketSupplementRef[],
    deliveryMethod?: ElectronicDeliveryMethod,
    refunds?: Refund[],
  } = {}): this {
    return GenericOrderTicketSupplementEntry.create({
      uniqueId,
      orderId,
      parentEntryId,
      entryGroupId,
      note,
      status,
      creatorId,
      creationDate,
      eventId,
      bookingStrategy,
      onlineTransactionDetails,
      offlineTransactionDetails,
      cancelledDate,
      cancelledBy,
      businessRef,
      supplements,
      deliveryMethod,
      refunds,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: GenericOrderEntryStatus.Aborted,
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GenericOrderEntryStatus.Refunded : GenericOrderEntryStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  copyWithOnlineTransactionDetails(transactionDetails: OnlineTransactionDetails): this {
    return this.copyWith({
      onlineTransactionDetails: transactionDetails,
    });
  }

  copyWithOfflineTransactionDetails(transactionDetails: OfflineTransactionDetails): this {
    return this.copyWith({
      offlineTransactionDetails: transactionDetails,
    });
  }

  isSame(other: GenericOrderTicketSupplementEntry): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.orderId === this.orderId &&
      other.parentEntryId === this.parentEntryId &&
      other.entryGroupId === this.entryGroupId &&
      other.note === this.note &&
      other.status === this.status &&
      other.creatorId === this.creatorId &&
      TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
      other.eventId === this.eventId &&
      other.bookingStrategy === this.bookingStrategy &&
      isSameData(other.onlineTransactionDetails, this.onlineTransactionDetails) &&
      isSameData(other.offlineTransactionDetails, this.offlineTransactionDetails) &&
      TimeZone.isSameInstantOrNull(other.cancelledDate, this.cancelledDate) &&
      other.cancelledBy === this.cancelledBy &&
      isSameData(other.businessRef, this.businessRef) &&
      isSameList(other.supplements, this.supplements, isSameData) &&
      other.deliveryMethod === this.deliveryMethod &&
      isSameList(other.refunds, this.refunds, isSameData);
  }

  tryGetApplicationFee(): Price | null {
    return null;
  }

  tryGetApplicationFeeVat(): InvoiceVat | null {
    return null;
  }

  tryGetRefunds(): Refund[] | null {
    return this.refunds;
  }

  tryAddRefund(refund: Refund): boolean {
    this.refunds.push(refund);
    return true;
  }

  tryGetEventId(): string | null {
    return this.eventId;
  }

  static verifyType(entry: DataModel): boolean {
    return entry instanceof GenericOrderTicketSupplementEntry;
  }

  static assertType(entry: DataModel): entry is GenericOrderTicketSupplementEntry {
    return entry instanceof GenericOrderTicketSupplementEntry;
  }
}

export class GenericOrderTicketSupplementEntryBuilder extends DataModelBuilder<GenericOrderTicketSupplementEntry> {
  build(context: SerializationContext, map: SerializedData): GenericOrderTicketSupplementEntry {
    return GenericOrderTicketSupplementEntry.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GenericOrderEntryStatus, GenericOrderEntryStatus.Open);

    if(versionRange.contains(GenericOrderTicketSupplementEntry.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrderTicketSupplementEntry.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `offlineTransactionDetails` of type `OfflineTransactionDetails | null`.
    if(version < 2) version = 2;

    // In version 3 we added `refunds` of type `Refund[]`.
    if(version < 3) version = 3;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
